import React, { useState, useEffect } from 'react';
import { Col, Card, CardBody, Alert, Badge, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import * as actions from '../../store/actions';
import { useDispatch } from 'react-redux';

export default function MaintenanceMode({ data }) {
  const { id, maintenance_mode } = data;
  const [maintenanceMode, setMaintenanceMode] = useState(maintenance_mode || false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setMaintenanceMode(maintenance_mode || false);
  }, [maintenance_mode]);

  const handleToggle = () => {
    if (isUpdating) return;

    const action = maintenanceMode ? 'تعطيل' : 'تفعيل';
    const message = `هل أنت متأكد من ${action} وضع الصيانة؟`;

    toast.warn(
      <div className="d-flex flex-column gap-2">
        <p className="mb-2">{message}</p>
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-warning btn-sm"
            onClick={() => {
              confirmToggle();
              toast.dismiss();
            }}
          >
            نعم، متأكد
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => toast.dismiss()}
          >
            إلغاء
          </button>
        </div>
      </div>,
      {
        position: 'top-center',
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
      }
    );
  };

  const confirmToggle = async () => {
    try {
      setIsUpdating(true);
      const prevMode = maintenanceMode;
      const newMode = !maintenanceMode;

      // Optimistic UI update
      setMaintenanceMode(newMode);

      // Dispatch the maintenance change to update platform_settings collection
      dispatch(actions.maintenance({ id, newMode }));

      toast.success(
        newMode
          ? '✓ تم تفعيل وضع الصيانة بنجاح'
          : '✓ تم تعطيل وضع الصيانة بنجاح',
        {
          icon: "✅",
          position: "top-center",
        }
      );
    } catch (error) {
      toast.error('حدث خطأ أثناء تغيير وضع الصيانة');
      // revert optimistic update
      setMaintenanceMode(prevMode || maintenance_mode || false);
    } finally {
      setTimeout(() => setIsUpdating(false), 1000);
    }
  };

  return (
    <Col xl={12} className="mb-4">
      <div className="d-flex align-items-center mb-3">
        <div className="avatar-sm me-3">
          <span className="avatar-title rounded-circle bg-warning-subtle text-warning font-size-20">
            <i className="bx bx-wrench"></i>
          </span>
        </div>
        <div>
          <h5 className="mb-1">وضع الصيانة</h5>
          <p className="text-muted mb-0">إيقاف التطبيق مؤقتاً للصيانة</p>
        </div>
      </div>

      <Card className="border shadow-sm" style={{ borderRadius: '12px' }}>
        <CardBody className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center mb-2">
                <Badge 
                  color={maintenanceMode ? "warning" : "success"} 
                  className="font-size-14 px-3 py-2 me-2"
                >
                  {maintenanceMode ? (
                    <>
                      <i className="bx bx-error-circle me-1"></i>
                      وضع الصيانة مفعّل
                    </>
                  ) : (
                    <>
                      <i className="bx bx-check-circle me-1"></i>
                      التطبيق يعمل بشكل طبيعي
                    </>
                  )}
                </Badge>
              </div>
              
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                {maintenanceMode ? (
                  <>
                    <i className="bx bx-info-circle me-1"></i>
                    التطبيق حالياً في وضع الصيانة. المستخدمون لا يمكنهم الوصول إلى الخدمات.
                  </>
                ) : (
                  <>
                    <i className="bx bx-check me-1"></i>
                    التطبيق متاح للمستخدمين ويعمل بشكل طبيعي.
                  </>
                )}
              </p>
            </div>

            <div className="form-check form-switch form-switch-lg" dir="ltr">
              {isUpdating ? (
                <Spinner size="sm" color="warning" />
              ) : (
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="maintenanceSwitch"
                  checked={maintenanceMode}
                  onChange={handleToggle}
                  disabled={isUpdating}
                  style={{ 
                    width: '3rem', 
                    height: '1.5rem',
                    cursor: isUpdating ? 'not-allowed' : 'pointer'
                  }}
                />
              )}
            </div>
          </div>

          {maintenanceMode && (
            <Alert color="warning" className="mt-3 mb-0 border-0" style={{ borderRadius: '8px' }}>
              <div className="d-flex align-items-start">
                <i className="bx bx-error-circle me-2 font-size-20"></i>
                <div>
                  <strong>تحذير:</strong> عند تفعيل وضع الصيانة، لن يتمكن المستخدمون من:
                  <ul className="mb-0 mt-2">
                    <li>تسجيل الدخول إلى التطبيق</li>
                    <li>إجراء حجوزات جديدة</li>
                    <li>الوصول إلى الخدمات</li>
                  </ul>
                </div>
              </div>
            </Alert>
          )}
        </CardBody>
      </Card>

      <Alert color="info" className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
        <i className="bx bx-info-circle me-2"></i>
        <strong>ملاحظة:</strong> استخدم وضع الصيانة عند إجراء تحديثات مهمة على النظام.
      </Alert>
    </Col>
  );
}
